import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SidebarMember from "../../components/Sidebar/SidebarMember";
import { db, auth } from "../../firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  doc,
  updateDoc,
  increment,
} from "firebase/firestore";

export default function PaymentMenu() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [requests, setRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("UPI");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'failure', null
  const user = auth.currentUser;

  // Fetch all groups
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "groups"), (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setGroups(data);
    });
    return () => unsub();
  }, []);

  // Fetch join requests for this member
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, "joinRequests"),
      where("memberId", "==", user.uid)
    );
    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRequests(data);
    });
    return () => unsub();
  }, [user]);

  // Fetch payments for this member
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, "payments"), where("memberId", "==", user.uid));
    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPayments(data);
    });
    return () => unsub();
  }, [user]);

  // Get active groups (accepted requests)
  const getActiveGroups = () => {
    return requests
      .filter((req) => req.status === "accepted")
      .map((req) => {
        const group = groups.find((g) => g.id === req.groupId);
        if (!group) return null;
        
        // Get last payment for this group
        const groupPayments = payments
          .filter((p) => p.groupId === req.groupId)
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        
        const lastPayment = groupPayments[0];
        const lastPaidDate = lastPayment?.createdAt?.toDate 
          ? new Date(lastPayment.createdAt.seconds * 1000) 
          : req.lastPaidAt?.toDate 
          ? new Date(req.lastPaidAt.seconds * 1000)
          : null;
        
        // Calculate due date (30 days from last payment or from request acceptance)
        const dueDate = lastPaidDate 
          ? new Date(lastPaidDate.getTime() + 30 * 24 * 60 * 60 * 1000)
          : req.createdAt?.toDate
          ? new Date(req.createdAt.seconds * 1000 + 30 * 24 * 60 * 60 * 1000)
          : null;
        
        const daysUntilDue = dueDate 
          ? Math.ceil((dueDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
          : null;
        
        // Determine payment status
        let status = "upcoming";
        if (daysUntilDue === null) status = "unknown";
        else if (daysUntilDue < 0) status = "overdue";
        else if (daysUntilDue <= 3) status = "due_soon";
        else status = "upcoming";

        return {
          ...group,
          request: req,
          lastPayment,
          lastPaidDate,
          dueDate,
          daysUntilDue,
          status,
          totalPayments: groupPayments.length,
        };
      })
      .filter((g) => g !== null);
  };

  // Get payment history (sorted by date, newest first)
  const getPaymentHistory = () => {
    return payments
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
      .map((payment) => ({
        ...payment,
        date: payment.createdAt?.toDate 
          ? new Date(payment.createdAt.seconds * 1000)
          : null,
      }));
  };

  // Get upcoming payments (due within next 7 days)
  const getUpcomingPayments = () => {
    return getActiveGroups()
      .filter((group) => {
        if (!group.dueDate) return false;
        const daysUntilDue = Math.ceil((group.dueDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        return daysUntilDue >= 0 && daysUntilDue <= 7;
      })
      .sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());
  };

  // Calculate payment progress for a group
  const getPaymentProgress = (group) => {
    if (!group.duration || !group.contribution) return 0;
    const expectedTotal = group.duration * group.contribution;
    const paidTotal = payments
      .filter((p) => p.groupId === group.id && p.status === "completed")
      .reduce((sum, p) => sum + (p.amount || 0), 0);
    return Math.min((paidTotal / expectedTotal) * 100, 100);
  };

  // Helper function to safely convert to Date
  const toDate = (dateValue) => {
    if (!dateValue) return new Date();
    if (dateValue instanceof Date) return dateValue;
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }
    if (dateValue.seconds) {
      return new Date(dateValue.seconds * 1000);
    }
    return new Date(dateValue);
  };

  // Handle groupId from URL parameter (when redirected from Dashboard or MyGroups)
  useEffect(() => {
    const groupId = searchParams.get("groupId");
    if (groupId && groups.length > 0 && requests.length > 0 && payments.length >= 0) {
      // Find the group in active groups
      const activeGroupsList = getActiveGroups();
      const groupToSelect = activeGroupsList.find((g) => g.id === groupId);
      
      if (groupToSelect) {
        setSelectedGroup(groupToSelect);
        setShowPaymentModal(true);
        setPaymentStatus(null);
        // Clear the URL parameter
        setSearchParams({});
      }
    }
  }, [searchParams, groups, requests, payments, setSearchParams]);

  // Handle Pay Now button click
  const handlePayNow = (group) => {
    setSelectedGroup(group);
    setShowPaymentModal(true);
    setPaymentStatus(null);
  };

  // Process payment with Razorpay
  const processPayment = async () => {
    if (!selectedGroup || !selectedGroup.contribution || selectedGroup.contribution <= 0) {
      alert("Invalid contribution amount");
      return;
    }

    // Configure Razorpay based on payment method
    const options = {
      key: "rzp_test_1DP5mmOlF5G1ag", // Replace with your Razorpay Key ID
      amount: selectedGroup.contribution * 100, // Amount in paise
      currency: "INR",
      name: selectedGroup.groupName,
      description: `Payment for ${selectedGroup.groupName}`,
      prefill: {
        name: user.displayName || "Member",
        email: user.email || "",
      },
      method: paymentMethod === "UPI" ? "upi" : paymentMethod === "Card" ? "card" : "netbanking",
      handler: async function (response) {
        try {
          // Payment successful - record in database
          await addDoc(collection(db, "payments"), {
            groupId: selectedGroup.id,
            groupName: selectedGroup.groupName,
            memberId: user.uid,
            memberName: user.displayName || "Member",
            amount: selectedGroup.contribution,
            status: "completed",
            paymentMethod: paymentMethod,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpayOrderId: response.razorpay_order_id || null,
            razorpaySignature: response.razorpay_signature || null,
            createdAt: serverTimestamp(),
          });

          // Update joinRequest (mark lastPaidAt)
          const req = requests.find(
            (r) => r.groupId === selectedGroup.id && r.memberId === user.uid
          );
          if (req) {
            const reqRef = doc(db, "joinRequests", req.id);
            await updateDoc(reqRef, {
              lastPaidAt: serverTimestamp(),
              paid: true,
            });
          }

          // Update group totals
          const groupRef = doc(db, "groups", selectedGroup.id);
          await updateDoc(groupRef, {
            totalCollected: increment(selectedGroup.contribution || 0),
            membersPaid: increment(1),
          });

          // Notify organizer
          if (selectedGroup.organizerId) {
            await addDoc(collection(db, "notifications"), {
              to: selectedGroup.organizerId,
              type: "payment",
              message: `${user.displayName || "Member"} paid ₹${selectedGroup.contribution} for ${selectedGroup.groupName}`,
              groupId: selectedGroup.id,
              memberId: user.uid,
              createdAt: serverTimestamp(),
              read: false,
            });
          }

          setPaymentStatus("success");
          setShowPaymentModal(false);
          setTimeout(() => {
            setPaymentStatus(null);
            setSelectedGroup(null);
          }, 3000);
        } catch (err) {
          console.error("Error recording payment:", err);
          setPaymentStatus("failure");
          alert("Payment completed but failed to record. Contact support with Transaction ID: " + response.razorpay_payment_id);
        }
      },
      modal: {
        ondismiss: function () {
          setPaymentStatus("failure");
          setShowPaymentModal(false);
          setTimeout(() => setPaymentStatus(null), 3000);
        },
      },
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error("Error opening Razorpay:", err);
      setPaymentStatus("failure");
      alert("Failed to open payment gateway. Please try again.");
    }
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "N/A";
    return toDate(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Format date with time
  const formatDateTime = (date) => {
    if (!date) return "N/A";
    return toDate(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return { bg: "#d4edda", color: "#155724", label: "Completed" };
      case "overdue":
        return { bg: "#f8d7da", color: "#721c24", label: "Overdue" };
      case "due_soon":
        return { bg: "#fff3cd", color: "#856404", label: "Due Soon" };
      case "upcoming":
        return { bg: "#d1ecf1", color: "#0c5460", label: "Upcoming" };
      default:
        return { bg: "#e2e3e5", color: "#383d41", label: "Unknown" };
    }
  };

  const activeGroups = getActiveGroups();
  const paymentHistory = getPaymentHistory();
  const upcomingPayments = getUpcomingPayments();

  // Styles
  const styles = {
    page: {
      flex: 1,
      padding: "30px",
      backgroundColor: "#f5f7fa",
      minHeight: "100vh",
      overflowY: "auto",
      marginLeft: "220px",
      width: "calc(100% - 220px)",
      boxSizing: "border-box",
    },
    header: {
      marginBottom: "30px",
    },
    pageTitle: {
      fontSize: "28px",
      fontWeight: "600",
      color: "#111827",
      margin: "0 0 8px 0",
    },
    subtitle: {
      fontSize: "14px",
      color: "#6b7280",
      margin: 0,
    },
    section: {
      backgroundColor: "#fff",
      borderRadius: "12px",
      padding: "24px",
      marginBottom: "24px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    },
    sectionTitle: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#111827",
      margin: "0 0 20px 0",
    },
    card: {
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      padding: "20px",
      marginBottom: "16px",
      backgroundColor: "#fff",
    },
    cardHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: "16px",
    },
    groupName: {
      fontSize: "18px",
      fontWeight: "600",
      color: "#111827",
      margin: "0 0 4px 0",
    },
    statusBadge: {
      padding: "6px 12px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: "500",
    },
    detailsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: "16px",
      marginBottom: "16px",
    },
    detailItem: {
      display: "flex",
      flexDirection: "column",
    },
    detailLabel: {
      fontSize: "12px",
      color: "#6b7280",
      marginBottom: "4px",
    },
    detailValue: {
      fontSize: "16px",
      fontWeight: "500",
      color: "#111827",
    },
    progressBar: {
      width: "100%",
      height: "8px",
      backgroundColor: "#e5e7eb",
      borderRadius: "4px",
      overflow: "hidden",
      marginBottom: "16px",
    },
    progressFill: {
      height: "100%",
      backgroundColor: "#2563eb",
      transition: "width 0.3s ease",
    },
    payButton: {
      backgroundColor: "#2563eb",
      color: "#fff",
      border: "none",
      padding: "12px 24px",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "background-color 0.2s ease",
      boxShadow: "0 2px 8px rgba(37,99,235,0.2)",
    },
    tableWrapper: {
      overflowX: "auto",
      width: "100%",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: "600px",
    },
    tableHeader: {
      backgroundColor: "#f5f7fa",
      padding: "12px",
      textAlign: "left",
      fontSize: "12px",
      fontWeight: "600",
      color: "#6b7280",
      borderBottom: "2px solid #e5e7eb",
    },
    tableCell: {
      padding: "12px",
      fontSize: "14px",
      color: "#111827",
      borderBottom: "1px solid #e5e7eb",
    },
    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 10000,
    },
    modalContent: {
      backgroundColor: "#fff",
      borderRadius: "12px",
      padding: "30px",
      maxWidth: "500px",
      width: "90%",
      maxHeight: "90vh",
      overflowY: "auto",
      boxShadow: "0 10px 30px rgba(2,6,23,0.2)",
    },
    modalTitle: {
      fontSize: "22px",
      fontWeight: "600",
      marginBottom: "8px",
      color: "#111827",
    },
    modalSubtitle: {
      fontSize: "14px",
      color: "#6b7280",
      marginBottom: "24px",
    },
    methodOption: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px 20px",
      border: "1px solid #e5e7eb",
      borderRadius: "10px",
      marginBottom: "12px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      backgroundColor: "#fff",
      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    },
    methodOptionSelected: {
      borderColor: "#2563eb",
      backgroundColor: "#eff6ff",
      boxShadow: "0 4px 12px rgba(37,99,235,0.2)",
      borderWidth: "2px",
    },
    methodOptionLeft: {
      display: "flex",
      alignItems: "center",
      flex: 1,
    },
    methodRadio: {
      marginRight: "16px",
      width: "20px",
      height: "20px",
      cursor: "pointer",
      accentColor: "#2563eb",
    },
    methodLabel: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#111827",
    },
    methodIcon: {
      fontSize: "24px",
      marginRight: "12px",
    },
    modalActions: {
      display: "flex",
      gap: "12px",
      marginTop: "24px",
    },
    cancelButton: {
      backgroundColor: "#f3f4f6",
      color: "#6b7280",
      border: "1px solid #e5e7eb",
      padding: "12px 24px",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    successMessage: {
      backgroundColor: "#d4edda",
      color: "#155724",
      padding: "16px",
      borderRadius: "8px",
      marginBottom: "20px",
      textAlign: "center",
    },
    failureMessage: {
      backgroundColor: "#f8d7da",
      color: "#721c24",
      padding: "16px",
      borderRadius: "8px",
      marginBottom: "20px",
      textAlign: "center",
    },
    emptyState: {
      textAlign: "center",
      padding: "40px",
      color: "#6b7280",
    },
  };

  return (
    <div style={{ display: "flex", width: "100%", minHeight: "100vh", position: "relative" }}>
      <SidebarMember />
      <div style={styles.page}>
        <div style={styles.header}>
          <h1 style={styles.pageTitle}>Payment Menu</h1>
          <p style={styles.subtitle}>Manage your payments and transactions</p>
        </div>

        {/* Payment Status Messages */}
        {paymentStatus === "success" && (
          <div style={styles.successMessage}>
            ✅ Payment successful! Transaction has been recorded.
          </div>
        )}
        {paymentStatus === "failure" && (
          <div style={styles.failureMessage}>
            ❌ Payment failed or was cancelled. Please try again.
          </div>
        )}

        {/* Current Payment Details Section */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Current Payments</h2>
          {activeGroups.length === 0 ? (
            <div style={styles.emptyState}>No active groups with payments due</div>
          ) : (
            activeGroups.map((group) => {
              const statusInfo = getStatusColor(group.status);
              const progress = getPaymentProgress(group);
              return (
                <div key={group.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <div>
                      <h3 style={styles.groupName}>{group.groupName}</h3>
                      <span
                        style={{
                          ...styles.statusBadge,
                          backgroundColor: statusInfo.bg,
                          color: statusInfo.color,
                        }}
                      >
                        {statusInfo.label}
                      </span>
                    </div>
                  </div>

                  <div style={styles.detailsGrid}>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>Amount</span>
                      <span style={styles.detailValue}>₹{group.contribution || 0}</span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>Due Date</span>
                      <span style={styles.detailValue}>
                        {group.dueDate ? formatDate(group.dueDate) : "N/A"}
                        {group.daysUntilDue !== null && (
                          <span style={{ fontSize: "12px", color: "#6b7280", marginLeft: "8px" }}>
                            ({group.daysUntilDue > 0 ? `${group.daysUntilDue} days left` : `${Math.abs(group.daysUntilDue)} days overdue`})
                          </span>
                        )}
                      </span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>Last Payment</span>
                      <span style={styles.detailValue}>
                        {group.lastPaidDate ? formatDate(group.lastPaidDate) : "Never"}
                      </span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>Total Payments</span>
                      <span style={styles.detailValue}>{group.totalPayments || 0}</span>
                    </div>
                  </div>

                  {/* Payment Progress Bar */}
                  <div style={styles.progressBar}>
                    <div
                      style={{
                        ...styles.progressFill,
                        width: `${progress}%`,
                      }}
                    />
                  </div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "16px" }}>
                    Progress: {progress.toFixed(1)}%
                  </div>

                  <button
                    style={styles.payButton}
                    onClick={() => handlePayNow(group)}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#1d4ed8";
                      e.target.style.boxShadow = "0 4px 12px rgba(37,99,235,0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#2563eb";
                      e.target.style.boxShadow = "0 2px 8px rgba(37,99,235,0.2)";
                    }}
                  >
                    Pay Now
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Upcoming Payments Section */}
        {upcomingPayments.length > 0 && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Upcoming Payments</h2>
            {upcomingPayments.map((group) => (
              <div key={group.id} style={styles.card}>
                <div style={styles.cardHeader}>
                  <div>
                    <h3 style={styles.groupName}>{group.groupName}</h3>
                  </div>
                </div>
                <div style={styles.detailsGrid}>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Amount</span>
                    <span style={styles.detailValue}>₹{group.contribution || 0}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Due Date</span>
                    <span style={styles.detailValue}>
                      {formatDate(group.dueDate)}
                      {group.daysUntilDue !== null && (
                        <span style={{ fontSize: "12px", color: "#6b7280", marginLeft: "8px" }}>
                          ({group.daysUntilDue} days)
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                <button
                  style={styles.payButton}
                  onClick={() => handlePayNow(group)}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#1d4ed8";
                    e.target.style.boxShadow = "0 4px 12px rgba(37,99,235,0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#2563eb";
                    e.target.style.boxShadow = "0 2px 8px rgba(37,99,235,0.2)";
                  }}
                >
                  Pay Now
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Payment History Section */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Payment History</h2>
          {paymentHistory.length === 0 ? (
            <div style={styles.emptyState}>No payment history available</div>
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>Transaction ID</th>
                    <th style={styles.tableHeader}>Group</th>
                    <th style={styles.tableHeader}>Amount</th>
                    <th style={styles.tableHeader}>Method</th>
                    <th style={styles.tableHeader}>Date</th>
                    <th style={styles.tableHeader}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((payment) => {
                    const statusInfo = getStatusColor(payment.status);
                    return (
                      <tr key={payment.id}>
                        <td style={styles.tableCell}>
                          {payment.razorpayPaymentId || payment.id.substring(0, 8)}
                        </td>
                        <td style={styles.tableCell}>{payment.groupName}</td>
                        <td style={styles.tableCell}>₹{payment.amount || 0}</td>
                        <td style={styles.tableCell}>
                          {payment.paymentMethod || "N/A"}
                        </td>
                        <td style={styles.tableCell}>
                          {payment.date ? formatDateTime(payment.date) : "N/A"}
                        </td>
                        <td style={styles.tableCell}>
                          <span
                            style={{
                              ...styles.statusBadge,
                              backgroundColor: statusInfo.bg,
                              color: statusInfo.color,
                            }}
                          >
                            {statusInfo.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Payment Method Selection Modal */}
        {showPaymentModal && selectedGroup && (
          <div style={styles.modal} onClick={() => setShowPaymentModal(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <h2 style={styles.modalTitle}>Select Payment Method</h2>
              <p style={styles.modalSubtitle}>
                Pay ₹{selectedGroup.contribution} for <strong>{selectedGroup.groupName}</strong>
              </p>

              <div style={{ marginBottom: "24px" }}>
                <div
                  style={{
                    ...styles.methodOption,
                    ...(paymentMethod === "UPI" ? styles.methodOptionSelected : {}),
                  }}
                  onClick={() => setPaymentMethod("UPI")}
                  onMouseEnter={(e) => {
                    if (paymentMethod !== "UPI") {
                      e.currentTarget.style.borderColor = "#2563eb";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(37,99,235,0.15)";
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (paymentMethod !== "UPI") {
                      e.currentTarget.style.borderColor = "#e5e7eb";
                      e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)";
                      e.currentTarget.style.transform = "translateY(0)";
                    }
                  }}
                >
                  <div style={styles.methodOptionLeft}>
                    <span style={styles.methodIcon}>💳</span>
                    <input
                      type="radio"
                      checked={paymentMethod === "UPI"}
                      onChange={() => setPaymentMethod("UPI")}
                      style={styles.methodRadio}
                    />
                    <span style={styles.methodLabel}>UPI</span>
                  </div>
                </div>
                <div
                  style={{
                    ...styles.methodOption,
                    ...(paymentMethod === "Card" ? styles.methodOptionSelected : {}),
                  }}
                  onClick={() => setPaymentMethod("Card")}
                  onMouseEnter={(e) => {
                    if (paymentMethod !== "Card") {
                      e.currentTarget.style.borderColor = "#2563eb";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(37,99,235,0.15)";
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (paymentMethod !== "Card") {
                      e.currentTarget.style.borderColor = "#e5e7eb";
                      e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)";
                      e.currentTarget.style.transform = "translateY(0)";
                    }
                  }}
                >
                  <div style={styles.methodOptionLeft}>
                    <span style={styles.methodIcon}>💳</span>
                    <input
                      type="radio"
                      checked={paymentMethod === "Card"}
                      onChange={() => setPaymentMethod("Card")}
                      style={styles.methodRadio}
                    />
                    <span style={styles.methodLabel}>Debit/Credit Card</span>
                  </div>
                </div>
                <div
                  style={{
                    ...styles.methodOption,
                    ...(paymentMethod === "Net Banking" ? styles.methodOptionSelected : {}),
                  }}
                  onClick={() => setPaymentMethod("Net Banking")}
                  onMouseEnter={(e) => {
                    if (paymentMethod !== "Net Banking") {
                      e.currentTarget.style.borderColor = "#2563eb";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(37,99,235,0.15)";
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (paymentMethod !== "Net Banking") {
                      e.currentTarget.style.borderColor = "#e5e7eb";
                      e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)";
                      e.currentTarget.style.transform = "translateY(0)";
                    }
                  }}
                >
                  <div style={styles.methodOptionLeft}>
                    <span style={styles.methodIcon}>🏦</span>
                    <input
                      type="radio"
                      checked={paymentMethod === "Net Banking"}
                      onChange={() => setPaymentMethod("Net Banking")}
                      style={styles.methodRadio}
                    />
                    <span style={styles.methodLabel}>Net Banking</span>
                  </div>
                </div>
              </div>

              <div style={styles.modalActions}>
                <button
                  style={styles.cancelButton}
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedGroup(null);
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#e5e7eb";
                    e.target.style.color = "#374151";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#f3f4f6";
                    e.target.style.color = "#6b7280";
                  }}
                >
                  Cancel
                </button>
                <button
                  style={styles.payButton}
                  onClick={processPayment}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#1d4ed8";
                    e.target.style.boxShadow = "0 4px 12px rgba(37,99,235,0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#2563eb";
                    e.target.style.boxShadow = "0 2px 8px rgba(37,99,235,0.2)";
                  }}
                >
                  Proceed to Pay
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

